import { Button, Modal, useTranslate } from "@tovi/ui";
import type { Cart } from "@/lib/cart.ts";

/**
 * Amending a line the station already has
 * (`08-pos-terminal-and-qr.md` § A, `PATCH /orders/{id}/items/{itemId}`).
 *
 * Two different acts, deliberately not one button. A **void** is a line that
 * should never have been rung — a mis-tap, a cashier correcting themselves. A
 * **return** is food that went out and came back, and it is gated on
 * `ORDER_RETURN` because a merchant buys it separately. The reports read them
 * apart, so the till must not blur them.
 *
 * Nothing is committed locally. The answer carries the whole order and the
 * store takes the lines from it, which is the only safe way to edit food that
 * is already being cooked: the server decides what the bill now says.
 *
 * **Built from the spec text.** Without a capture, this is a modal reached by
 * tapping the quantity on a fired line — the one part of that row which is not
 * already the item's name or its price. A real till may want a swipe. The
 * reason field the contract allows is not collected; `reason` goes as null,
 * and a merchant who needs one has the order surface.
 */
export function LineActionsDialog({
  cart,
  itemId,
  onClose,
}: {
  cart: Cart;
  itemId: string;
  onClose: () => void;
}) {
  const t = useTranslate();
  const line = cart.lines.find((row) => row.key === itemId);

  function amend(action: "void" | "return"): void {
    cart.amendLine(itemId, action);
    onClose();
  }

  return (
    <Modal open title={t("pos.line.title")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-body">{line?.name ?? ""}</p>

        {/* Neither button is gated, and that is not an omission. Both send
            `PATCH /orders/{id}/items/{itemId}`, which checks `orders:write` —
            the key every role that reaches a till already holds.
            `orders:void` and `orders:refund` guard cancelling and refunding a
            whole *bill*, which this screen does not offer. Gating on a key the
            server does not check here would hide a button that works, which is
            the same lie as showing one that does not. */}
        <div className="flex flex-col gap-2">
          <Button variant="secondary" onClick={() => amend("void")}>
            {t("pos.line.void")}
          </Button>
          <Button variant="secondary" onClick={() => amend("return")}>
            {t("pos.line.return")}
          </Button>
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t("pos.line.back")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
